// Purpose of This File: Enhancing Product Listing Page (PLP) UI
// This file was specifically created to enhance the User Interface (UI) of the Product Listing Page (PLP).
// The existing product details were minimal, so this new file aligns with the full - fledged UI design for product representation.

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import Image, { ImageProps } from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop, mediumDesktop, tablet } from 'helpers/utils/screensizes';
import useTrack from '../../../product-tile/useTrack';
// import { CenterImageCropSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import { CheckedSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import Prices from '../../../product-tile/prices';
// import Link from 'components/commercetools-ui/atoms/link';
import Link from 'next/link';
import Price from '../Algolia-search-Page/price';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import aa from 'search-insights';
import useLocale from 'helpers/hooks/useLocale';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import Loading from 'components/commercetools-ui/atoms/loading';
import PriceMap from '../AlgoliaPrice/priceMap';
import { useCookies } from 'react-cookie';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { Swatch } from 'helpers/algolia/map-product';

import { getHexCode } from 'helpers/utils/getHexcode';
import { CLICKED_OBJECT_ID, SEARCH_QUERY_ID } from 'helpers/constants/algolia';
import { CartDiscount } from '../../../product-details/types';
import { P2Badge } from '../../types';
import { mobile } from 'helpers/utils/screensizes';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import { fetchTrustPilotData } from 'helpers/utils/trustPilotReview';
import { usePathname } from 'next/navigation';

interface ProductListTileProps {
  product: Product;
  productLogo?: ImageProps;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  onCompareChange?: (product: Product, checked: boolean) => void;
  isCompared?: boolean;
  handleCompareChange: (product: Product) => void;
  setOpen: (b: boolean) => void;
  compareProduct: any;
  p1BadgeData?: any;
  p2BadgeData?: P2Badge[];
  slug?: string;
  queryId?: string;
  position?: number;
  trustBoxCss?: TrustBoxCss;
}

const ProductListTile: FC<ProductListTileProps> = ({
  handleCompareChange,
  product,
  productLogo,
  onClick,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  onCompareChange,
  isCompared = false,
  setOpen,
  compareProduct,
  p1BadgeData,
  p2BadgeData,
  slug,
  queryId,
  position,
  trustBoxCss,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [isInDesktopRange] = useMediaQuery([desktop, mediumDesktop - 1]);
  const [isMobile] = useMediaQuery(mobile);
  const { ref } = useTrack({ product });
  const { _url } = product;
  const urlArray = _url?.split('/') ?? [];
  const catUrl = slug?.split('_')?.[0];
  const [imageError, setImageError] = useState(false);
  const pdpUrl = `/${catUrl}/${urlArray[urlArray?.length - 1]}`;
  const variantWithDiscount = useVariantWithDiscount(product?.variants) as Variant;
  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [hasReviews, setHasReviews] = useState(false);
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');
  const [trustPilotRes, setTrustPilotRes] = useState<any>();
  const [isMobileView] = useMediaQuery(mobile);
  const productOffers = product?.offer as Array<{ id: string; description: string }> | undefined;
  const hasOffer = (productOffers?.length ?? 0) > 0;
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  //   const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  //   const [hoveredVariant, setHoveredVariant] = useState<Variant | null>(null); // Initialize with null
  //   const [selectedColour, setSelectedColour] = useState<string | null>(
  //     variantWithDiscount ? variantWithDiscount?.attributes?.colour : product?.variants[0]?.attributes?.colour || null,
  //   );
  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  const [selectedSwatch, setSelectedSwatch] = useState<Swatch | null | undefined>(() =>
    product?.swatches ? product?.swatches?.find((swatch) => swatch?.variantCode === product?.variantCode) : null,
  );
  const [hoveredSwatch, setHoveredSwatch] = useState<Swatch | null>(null); // Initialize with null
  const [selectedColour, setSelectedColour] = useState<string | null>(
    product?.swatches ? product?.swatches[0]?.colourHexCode : '',
  );
  const { loggedIn, result } = useAccount();
  const { locale, getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const isProductInCompare = compareProduct?.some((p: any) => p?.productKey === product?.productKey);
  // const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);
  const [StarImage, setStarImage] = useState<React.FC | null>(null);
  const [starsAverage, setStarsAverage] = useState<number | null>(null);
  const [numberOfReviews, setNumberOfReviews] = useState<number | null>(null);
  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);
  const targetVariant = product?.variants?.find((attributes) => attributes?.sku === selectedSwatch?.sku);
  const now = new Date();
  const pathname = usePathname()?.toLowerCase() || '';

  // const isNew = useMemo(() => {
  //   const dateStr = product?.variants?.find((v: Variant) => v?.attributes?.firstReceiptDate)?.attributes
  //     ?.firstReceiptDate;
  //   if (!dateStr) return false;
  //   const firstReceipt = new Date(dateStr as string);
  //   if (isNaN(firstReceipt.getTime())) return false;
  //   const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  //   return firstReceipt >= twoMonthsAgo && firstReceipt <= now;
  // }, []);
  const isNew = useMemo(() => {
    const categories = (product?.categories as any)?.['en-GB']?.lvl0;
    if (!Array.isArray(categories)) return false;
    return categories?.some((category: string) => category?.toLowerCase?.().includes('new arrivals'));
  }, []);
  const isSale = useMemo(() => {
    const categories = (product?.categories as any)?.['en-GB']?.lvl0;
    if (!Array.isArray(categories)) return false;
    return categories.some((category: string) => category?.toLowerCase?.().includes('sale'));
  }, []);
  const isCustomFitProduct = useMemo(() => {
    return product?.customFit === 'custom' || false;
  }, [product?.customFit]);
  const basedOnCategories = useCallback(
    (badge: any) => {
      const matchCategories = badge?.matchCategories || badge?.conditions?.matchCategories;
      return (
        Array.isArray(matchCategories) && matchCategories.some((cat: string) => pathname?.includes(cat?.toLowerCase()))
      );
    },
    [pathname],
  );
  const activeP1Badges = useMemo(() => {
    if (!p1BadgeData) return [];
    return p1BadgeData?.filter(
      (badgemap: any) => badgemap?.variantCodes?.includes(selectedSwatch?.variantCode) || basedOnCategories(badgemap),
    );
  }, [p1BadgeData, selectedSwatch?.variantCode]);
  const activeP2Badges = useMemo(() => {
    if (!p2BadgeData) return [];
    const getBadge = (id: string, condition: boolean) =>
      condition
        ? p2BadgeData.find((badge: P2Badge) => badge?.id === id)
        : p2BadgeData.find((badge: P2Badge) => basedOnCategories(badge));

    const newBadge = getBadge('new', isNew);
    const saleBadge = getBadge('sale', isSale);
    const customFitBadge = isCustomFitProduct ? p2BadgeData.find((badge: P2Badge) => badge?.id === 'customFit') : null;
    const uniqueBadges = [newBadge, saleBadge, customFitBadge]
      .filter(Boolean)
      .filter((badge, idx, arr) => arr.findIndex((b: any) => b.id === badge?.id) === idx);

    return uniqueBadges;
  }, [p2BadgeData, isNew, isSale, isCustomFitProduct, basedOnCategories]);
  const allBadges = useMemo(() => {
    return [...activeP1Badges, ...activeP2Badges];
  }, [activeP1Badges, activeP2Badges]);
  const getRanking = useCallback((badge: any) => {
    return Number(badge?.ranking ?? 99);
  }, []);
  const sortedBadges = useMemo(() => {
    return allBadges?.sort((a, b) => getRanking(a) - getRanking(b));
  }, [allBadges]);

  const imageBadges = sortedBadges.filter(
    (badge) => (isDesktopSize ? badge.visibility?.desktop?.container : badge.visibility?.mobile?.container) === 'image',
  );

  const menuBadges = sortedBadges.filter(
    (badge) => (isDesktopSize ? badge.visibility?.desktop?.container : badge.visibility?.mobile?.container) === 'menu',
  );
  const visibleImageBadges = imageBadges.filter((badge) => {
    const { isHidden, validFrom, validTo } = badge.conditions || {};
    if (isHidden) return false;
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    return (!from || from <= now) && (!to || to >= now);
  });
  const groupedImageBadges = visibleImageBadges.reduce((acc, badge) => {
    const position = isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position;
    if (position && !acc[position]) {
      acc[position] = [badge];
    }
    return acc;
  }, {} as Record<string, any[]>);
  const isBottomCenterBadgeVisible = Object.keys(groupedImageBadges)?.some(
    ([position]) => position === 'bottom-center',
  );
  const visibleMenuBadges = menuBadges.filter((badge) => {
    const { isHidden, validFrom, validTo } = badge.conditions || {};
    if (isHidden) return false;
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    return (!from || from <= now) && (!to || to >= now);
  });
  const cartDiscountBadge = p1BadgeData?.find((badge: any) => badge?.label?.[selectedLocale] === 'cart discount');
  const bottomCenterBadges = {
    'bottom-center': groupedImageBadges['bottom-center'] || [],
  };
  const otherPositionBadges = Object.entries(groupedImageBadges)
    .filter(([position]) => position !== 'bottom-center')
    .reduce((acc, [position, badges]) => {
      acc[position] = badges;
      return acc;
    }, {} as typeof groupedImageBadges);

  const cartDiscount = (targetVariant?.attributes?.cartDiscount &&
    JSON.parse(targetVariant?.attributes?.cartDiscount as string)) as CartDiscount[];
  const getPositionClass = (position: string) => {
    switch (position) {
      case 'top-right':
        return 'top-0 right-0 items-end';
      case 'bottom-center':
        return 'bottom-0 left-1/2 transform -translate-x-1/2 items-center';
      case 'left':
        return 'top-1/2 left-0 transform -translate-y-1/2';
      case 'center':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'right':
        return 'top-1/2 right-0 transform -translate-y-1/2';
      default:
        return 'top-0 right-0';
    }
  };

  useEffect(() => {
    if (hoveredSwatch) {
      const filteredPrice: Swatch | undefined = product?.swatches?.find(
        (price: Swatch) => price?.sku === hoveredSwatch?.sku,
      );
      const isGBP = cookies?.locale_selection?.currency === 'GBP';

      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.prices?.[0]?.priceGB : filteredPrice?.prices?.[0]?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.prices?.[0]?.mrrpPriceGB : filteredPrice?.prices?.[0]?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.prices?.[0]?.wasPriceGB : filteredPrice?.prices?.[0]?.wasPriceEU);
    }
  }, [hoveredSwatch]);
  const mrrpPounds = mrrpPrice / Math.pow(10, 2);
  const pricePounds = price / Math.pow(10, 2);

  // const handleNavigation = (link: string) => {
  //   // window.location.href = link;
  //   // setIsLoading(true);
  //   // const timeoutId = setTimeout(() => {
  //   //   setIsLoading(false);
  //   // }, 1000);

  //   // return () => clearTimeout(timeoutId);
  //   // setIsLoading(true);
  //   requestAnimationFrame(() => {
  //     window.location.href = link;
  //   });
  // };
  // const buttonIsVisible = useMemo(
  //   () => (imageHovered || buttonHovered) && isDesktopSize,
  //   [imageHovered, buttonHovered, isDesktopSize],
  // );

  // const handleNavigation = (link: string) => {
  //   // window.location.href = link;
  //   // setIsLoading(true);
  //   // const timeoutId = setTimeout(() => {
  //   //   setIsLoading(false);
  //   // }, 1000);

  //   // return () => clearTimeout(timeoutId);
  //   // setIsLoading(true);
  //   requestAnimationFrame(() => {
  //     window.location.href = link;
  //   });
  // };
  // const buttonIsVisible = useMemo(
  //   () => (imageHovered || buttonHovered) && isDesktopSize,
  //   [imageHovered, buttonHovered, isDesktopSize],
  // );

  // const handleColourSelect = (variant: Variant, objectId: string) => {
  //     handleProductSwatchClick(locale, [objectId]);
  //     setSelectedColour(variant?.attributes?.colour);
  //     setSelectedVariant(variant);
  // };

  const handleColourSelect = (swatch: Swatch, objectId: string) => {
    handleProductSwatchClick(locale, [objectId]);
    setSelectedColour(swatch?.colourHexCode);
    setSelectedSwatch(swatch);
  };

  // const uniqueColorVariants = useMemo(() => {
  //     const seenColours = new Set();
  //     return product.variants.filter((variant: Variant) => {
  //         const colour = variant?.attributes?.colour;
  //         if (!colour || seenColours.has(colour)) {
  //             return false;
  //         }
  //         seenColours.add(colour);
  //         return true;
  //     });
  // }, [product.variants]);

  // const uniqueColorVariants = useMemo(() => {
  //     const seenColours = new Set();
  //     return product?.swatches?.filter((swatch: Swatch) => {
  //         const colour = swatch?.variantCode;
  //         if (!colour || seenColours.has(colour)) {
  //             return false;
  //         }
  //         seenColours.add(colour);
  //         return true;
  //     });
  // }, [product.swatches]);

  const uniqueColorVariants: Swatch[] = useMemo(() => {
    return product?.swatches?.filter((swatch) => swatch?.colour) ?? [];
  }, [product?.swatches]);

  const sortedColorVariants = useMemo(() => {
    if (!uniqueColorVariants || !selectedSwatch) return uniqueColorVariants ?? [];
    const selectedSwatchIndex = uniqueColorVariants?.findIndex(
      (swatch) => swatch?.variantCode === selectedSwatch?.variantCode,
    );
    if (selectedSwatchIndex === -1) return uniqueColorVariants;
    return [
      uniqueColorVariants?.[selectedSwatchIndex],
      ...uniqueColorVariants?.slice(0, selectedSwatchIndex),
      ...uniqueColorVariants?.slice(selectedSwatchIndex + 1),
    ];
  }, []);

  // const sortedSwatches = useMemo(() => {
  //     if (!uniqueColorVariants || !selectedSwatch?.colourHexCode) return uniqueColorVariants;

  //     return [
  //         ...uniqueColorVariants?.filter((swatch) => swatch?.colourHexCode === selectedSwatch?.colourHexCode),
  //         ...uniqueColorVariants?.filter((swatch) => swatch?.colourHexCode !== selectedSwatch?.colourHexCode),
  //     ];
  // }, [uniqueColorVariants, selectedSwatch]);

  const badgeStyles = {
    BLACKNOVEMBER: 'bg-black text-white h-[22px] w-[95px] md:h-[30px] md:w-[160px]',
    SALE: 'bg-[#E30616] text-white h-[22px] w-[28px] md:h-[30px] md:w-[67px]',
    NEW: 'bg-[#137E57] text-white h-[22px] w-[38px] md:h-[30px] md:w-[68px]',
    MEGADEALS: 'bg-gradient-to-r from-[#272C59] to-[#BD2F48] text-white h-[22px] w-[65px] md:h-[30px] md:w-[112px]',
    CHIRSTMAS: 'bg-[#137E57] text-white h-[22px] w-[65px] md:h-[30px] md:w-[120px]',
    TOPGIFT: 'bg-[#D9BE65] text-black h-[20px] w-[53px] md:h-[30px] md:w-[93px]',
    BANKHOLIDAY: 'bg-[#BD2F48] text-white h-[22px] w-[79px] md:h-[30px] md:w-[134px]',
    BANKHOLIDAYDEAL: 'bg-[#BD2F48] text-white h-[22px] w-[103px] md:h-[30px] md:w-[173px]',
    FREEACCESSORYPACK: 'bg-[#1E1E1E] text-white h-[22px] w-[115px] md:h-[30px] md:w-[193px]',
  };
  const userId = loggedIn ? result?.data?.data?.account?.accountId : localStorage.getItem('__anon_id');
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const handleProductClick =
    (locale: string, productId: string | undefined) => (event: React.MouseEvent<HTMLDivElement>) => {
      // setIsLoading(true);
      const analytics = new AlgoliaAnalytics(locale);
      if (isSearchResult && productId && queryId && position) {
        analytics?.handleClickAfterSearch([productId], userId, queryId, [position]);
      } else {
        analytics?.handleProductClick(productId, queryId, position);
      }
      if (productId) sessionStorage?.setItem(CLICKED_OBJECT_ID, productId);
      if (queryId) sessionStorage?.setItem(SEARCH_QUERY_ID, queryId);
    };

  const handleProductSwatchClick = (locale: string, objectId: string[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    if (isSearchResult && objectId?.length > 0 && queryId && position) {
      analytics?.handleColourSwatchesClickedAfterSearch(objectId, userId, queryId, position);
    } else {
      analytics?.handleColourSwatchesClickedInPLP(objectId, userId);
    }
  };

  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price || mrrpPrice < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);

  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);

  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice?.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    const filteredPrice = product?.swatches?.find((price: Swatch) => price?.sku === selectedVariant?.sku);

    if (filteredPrice) {
      // Set the states based on the selected currency
      setPrice(isGBP ? selectedSwatch?.prices?.[0]?.priceGB : selectedSwatch?.prices?.[0]?.priceEU);
      setMrrpPrice(isGBP ? selectedSwatch?.prices?.[0]?.mrrpPriceGB : selectedSwatch?.prices?.[0]?.mrrpPriceEU);
      setWasPrice(isGBP ? selectedSwatch?.prices?.[0]?.wasPriceGB : selectedSwatch?.prices?.[0]?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
  }, [product?.standalonePrice, selectedSwatch?.sku, cookies?.locale_selection?.currency]);

  const formatDescription = (desc?: string) => {
    if (!desc) return null;

    let parts: string[] = [];
    if (desc?.includes('\n')) {
      parts = desc?.split('\n')?.map((part) => part?.trim()?.replace(/,+$/, ''));
    } else if (desc?.includes(',')) {
      parts = desc
        ?.split(',')
        ?.map((part) => part?.trim()?.replace(/,+$/, ''))
        ?.filter((part) => part?.length > 0);
    } else {
      parts = [desc?.trim()?.replace(/,+$/, '')];
    }

    return parts?.map((part, idx) => (
      <span key={idx}>
        {part}
        {idx < parts?.length - 1 && <br />}
      </span>
    ));
  };

  const getDescriptionFromLocale = (
    description: Record<string, string>,
    locale: string | undefined,
  ): string | undefined => {
    if (!locale) return undefined;

    const normalizedLocale = locale?.replace('_', '-');
    return description?.[normalizedLocale];
  };
  // const cartDiscount: string[] = ((product?.offer as { id: string; description: string }[]) || [])
  //   ?.map((offer) => offer?.description)
  //   ?.filter((desc: string) => desc !== '');

  const productColour = selectedSwatch?.colour || product?.colourDisplay || '';
  const productLink = isCustomFitProduct
    ? product?._url ?? ''
    : `${product?._url ?? ''}?variantCode=${selectedSwatch?.variantCode ?? ''}`;
  const isPLP = true;
  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchTrustPilotData(product, undefined, isPLP);
      if (result) {
        setTrustPilotRes(result?.raw);
        setStarsAverage(result?.starsAverage);
        setNumberOfReviews(result?.numberOfReviews);
        setStarImage(() => result?.StarImageComponent);
      }
    };

    fetchData();
  }, [product]);

  return (
    <>
      {/* {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : ( */}
      <div
        ref={ref}
        className="flex h-full flex-col border p-8  sm:p-0 md:border-0"
        // onClick={() => {
        //   handleProductClick(locale, product?.objectId);
        //   handleNavigation(
        //     `${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`,
        //   );
        // }}
      >
        <SmartLink
          // href={isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}
          // href={
          //     isCustomFitProduct
          //         ? product?._url ?? ''
          //         : `${product?._url ?? ''}?variant=${selectedVariant?.sku ?? product?.standalonePrice?.[0]?.variantSku ?? ''
          //         }`
          // }
          href={productLink}
          callback={handleProductClick(locale, product?.objectId)}
          // onClick={(e) => {
          //   // Detect "open in new tab" or non-left-clicks
          //   if (
          //     e.metaKey || // ⌘ Cmd on Mac
          //     e.ctrlKey || // Ctrl on Windows/Linux
          //     e.shiftKey || // Shift+Click
          //     e.button !== 0 // Middle click (button 1), right click (button 2)
          //   ) {
          //     // Let the browser handle it normally
          //     return;
          //   }

          //   // Prevent default only for left-click navigation
          //   e.preventDefault();
          //   handleProductClick(locale, product?.objectId);
          //   handleNavigation(
          //     `${
          //       isCustomFitProduct
          //         ? product?._url
          //         : `${product?._url}?variant=${
          //             selectedVariant?.sku ? selectedVariant?.sku : product?.standalonePrice?.[0]?.variantSku
          //           }`
          //     }`,
          //     // product?._url ?? '',
          //   );
          // }}
        >
          <div className="relative flex flex-col">
            {/* <Link
              link={`${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`}
            > */}
            <div
              className="relative w-full p-8"
              onMouseEnter={() => setImageHovered(true)}
              onMouseLeave={() => setImageHovered(false)}
            >
              <div
                className="relative w-full"
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
              >
                <div className="relative  bg-white">
                  <div className="relative w-full">
                    <Image
                      // src={hoveredVariant?.images?.[0] || selectedVariant.images?.[0] || productLogo?.media?.file}
                      src={
                        hoveredSwatch?.images?.[0] ||
                        (selectedSwatch?.images ? selectedSwatch?.images?.[0] : null) ||
                        productLogo?.media?.file ||
                        (product.colourImages && product?.colourImages[0])
                      }
                      suffix="medium"
                      alt={product.name ?? ''}
                      style={{ objectFit: 'contain', objectPosition: 'center' }}
                      className="w-full rounded-sm group-hover:opacity-75 sm:h-[164px] md:h-[314px]"
                    />
                    {typeof discountPercentage === 'number' &&
                      discountPercentage > 0 &&
                      typeof mrrpPoundsDiscount === 'number' &&
                      mrrpPoundsDiscount && (
                        <div className="absolute left-0 top-0 z-10 flex h-[20px] min-w-[39px] items-center justify-center gap-8 self-stretch rounded-[2px] border border-[#686868] bg-white px-6 py-8 sm:left-2 sm:top-2 sm:h-[22px] sm:min-w-[46px]">
                          {/* <div className=" h-[20px] min-w-[39px] lg:h-[22px] lg:min-w-[46px]"> */}
                          <p
                            className={`text-center font-body text-11 font-bold not-italic leading-[110%] text-[#686868] sm:text-14`}
                          >
                            {mrrpPounds < 200
                              ? `-${discountPercentage}%`
                              : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                                  mrrpPoundsDiscount,
                                )}`}
                          </p>
                          {/* </div> */}
                        </div>
                      )}
                    {
                      <div>
                        {hasOffer && isDesktopSize && (
                          <div className="absolute bottom-0 flex w-full flex-col items-center justify-center">
                            <div
                              style={{
                                backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                                color: cartDiscountBadge?.styles?.textColor,
                                padding: !isDesktopSize
                                  ? cartDiscountBadge?.styles?.paddingMobile
                                  : cartDiscountBadge?.styles?.paddingDesktop,
                                borderRadius: cartDiscountBadge?.styles?.borderRadius,
                                fontSize: !isDesktopSize
                                  ? cartDiscountBadge?.styles?.fontSizeMobilePx
                                  : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                                fontWeight: cartDiscountBadge?.styles?.fontWeight,
                                letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                                minHeight: !isDesktopSize
                                  ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                                  : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                                maxHeight: !isDesktopSize
                                  ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                                  : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                              }}
                              className="flex flex-col items-center justify-center whitespace-normal break-words text-center"
                            >
                              {formatDescription(productOffers?.[0]?.description)}
                            </div>

                            <div className="flex flex-col items-center gap-y-2">
                              {Object.entries(bottomCenterBadges).map(([position, badges]) =>
                                (badges as any[])?.map((item: any, index: number) => {
                                  const styles = item?.styles;
                                  const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                                  const iconAsset = item?.icon?.asset?.[selectedLocale];
                                  const iconSize = isDesktopSize
                                    ? Math.min(item?.icon?.iconSizeDesktopPx ?? 0, 22)
                                    : Math.min(item?.icon?.iconSizeMobilePx ?? 0, 18);
                                  const iconPosition = item?.icon?.position || 'left';
                                  const iconType = item?.icon?.positioning?.type || 'relative';
                                  const iconStyle: React.CSSProperties = {
                                    position: iconType,
                                    top: item?.icon?.positioning?.top ?? 0,
                                    left: item?.icon?.positioning?.left ?? 0,
                                    width: iconSize,
                                    height: iconSize,
                                    ...(iconPosition === 'left' && { marginRight: item?.icon?.spacingPx ?? 0 }),
                                    ...(iconPosition === 'right' && { marginLeft: item?.icon?.spacingPx ?? 0 }),
                                  };

                                  const inlineStyles = {
                                    ...(isGradient
                                      ? { backgroundImage: styles?.backgroundGradient }
                                      : { backgroundColor: styles?.backgroundColor }),
                                    color: styles?.textColor,
                                    fontSize: `${
                                      !isDesktopSize ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx
                                    }px`,
                                    fontWeight: styles?.fontWeight,
                                    padding: !isDesktopSize ? styles?.paddingMobile : styles?.paddingDesktop,
                                    minHeight: `${
                                      !isDesktopSize ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx
                                    }px`,
                                    maxHeight: `${
                                      !isDesktopSize ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx
                                    }px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: `${!isDesktopSize ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                                    borderWidth: `${styles?.borderThickness || 0}px`,
                                    borderColor: styles?.borderColor,
                                    borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                                    letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                                  };

                                  const label = item?.label?.[selectedLocale] || '';
                                  const shouldUseLineBreak = isDesktopSize
                                    ? styles?.manualLineBreakDesktop
                                    : styles?.manualLineBreakMobile;

                                  return (
                                    <div
                                      key={item?.label?.[selectedLocale] || index}
                                      style={inlineStyles}
                                      className="w-fit"
                                    >
                                      {iconPosition === 'left' && iconAsset && (
                                        <img
                                          src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                                          alt=""
                                          width={iconSize}
                                          height={iconSize}
                                          style={iconStyle}
                                        />
                                      )}
                                      {shouldUseLineBreak ? (
                                        <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                                      ) : (
                                        <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                                      )}
                                      {iconPosition === 'right' && iconAsset && (
                                        <img
                                          src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                                          alt=""
                                          width={iconSize}
                                          height={iconSize}
                                          style={iconStyle}
                                        />
                                      )}
                                    </div>
                                  );
                                }),
                              )}
                            </div>
                          </div>
                        )}
                        {Object.entries(otherPositionBadges).map(([position, badges]) => (
                          <div
                            key={position}
                            className={`absolute flex w-fit flex-col gap-y-4 ${getPositionClass(position)}`}
                          >
                            {(badges as any[])?.map((item: any, index: number) => {
                              const styles = item?.styles;
                              const desktopPosition = item?.visibility?.desktop?.position;
                              const mobilePosition = item?.visibility?.mobile?.position;
                              const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                              const iconPosition = item?.icon?.position || 'left';
                              const iconType = item?.icon?.positioning?.type || 'relative';
                              const iconAsset = item?.icon?.asset?.[selectedLocale];
                              const iconSize = isDesktopSize
                                ? Math.min(item?.icon?.iconSizeDesktopPx ?? 0, 22)
                                : Math.min(item?.icon?.iconSizeMobilePx ?? 0, 18);
                              const iconPositioning = item?.icon?.positioning?.[isMobile ? 'mobile' : 'desktop'] || {
                                top: 0,
                                left: 0,
                              };

                              const inlineStyles = {
                                ...(isGradient
                                  ? { backgroundImage: styles?.backgroundGradient }
                                  : { backgroundColor: styles?.backgroundColor }),
                                color: styles?.textColor,
                                fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                                fontWeight: styles?.fontWeight,
                                padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                                minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                                maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                                borderWidth: `${styles?.borderThickness || 0}px`,
                                borderColor: styles?.borderColor,
                                borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                                letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                              };
                              const shouldUseLineBreak = isDesktopSize
                                ? styles?.manualLineBreakDesktop
                                : styles?.manualLineBreakMobile;
                              const iconStyle: React.CSSProperties = {
                                position: iconType,
                                top: iconPositioning.top ?? 0,
                                left: iconPositioning.left ?? 0,
                                width: iconSize,
                                height: iconSize,
                                ...(iconPosition === 'left' && { marginRight: item?.icon?.spacingPx ?? 0 }),
                                ...(iconPosition === 'right' && { marginLeft: item?.icon?.spacingPx ?? 0 }),
                              };
                              const iconElement = iconAsset && (
                                <img
                                  src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                                  alt=""
                                  width={iconSize}
                                  height={iconSize}
                                  style={iconStyle}
                                />
                              );

                              const label = item?.label?.[selectedLocale] || '';

                              return (
                                <div
                                  key={item?.label?.[selectedLocale] || index}
                                  style={inlineStyles}
                                  className="w-fit"
                                >
                                  {iconPosition === 'left' && iconElement}
                                  {shouldUseLineBreak ? (
                                    <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                                  ) : (
                                    <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                                  )}{' '}
                                  {iconPosition === 'right' && iconElement}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    }
                  </div>
                </div>
              </div>
              {/* </Link> */}
            </div>

            {/* <Link link={`${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}> */}
            <div className={`flex flex-col gap-16 p-8 text-center`}>
              <div className="flex flex-col gap-y-8">
                {!isDesktopSize && (
                  <div>
                    {hasOffer && (
                      <div className="flex w-full flex-col items-center justify-center gap-4">
                        <div
                          style={{
                            backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                            color: cartDiscountBadge?.styles?.textColor,
                            padding: !isDesktopSize
                              ? cartDiscountBadge?.styles?.paddingMobile
                              : cartDiscountBadge?.styles?.paddingDesktop,
                            borderRadius: cartDiscountBadge?.styles?.borderRadius,
                            fontSize: !isDesktopSize
                              ? cartDiscountBadge?.styles?.fontSizeMobilePx
                              : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                            fontWeight: cartDiscountBadge?.styles?.fontWeight,
                            letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                            minHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                            maxHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                          }}
                          className="flex flex-col items-center justify-center whitespace-normal break-words text-center"
                        >
                          {formatDescription(productOffers?.[0]?.description)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {visibleMenuBadges.length > 0 && (
                  <div className="flex flex-col gap-y-8">
                    {visibleMenuBadges?.slice(0, 2)?.map((badge, idx) => {
                      const iconPosition = badge?.icon?.position || 'left';
                      const label = badge?.label?.[selectedLocale];
                      const containerPosition =
                        (isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position) ||
                        'center';
                      const iconType = badge?.icon?.positioning?.type || 'relative';
                      const iconPositioning = badge?.icon?.positioning?.[isDesktopSize ? 'desktop' : 'mobile'] || {
                        top: 0,
                        left: 0,
                      };
                      const iconSize = isDesktopSize
                        ? Math.min(badge?.icon?.iconSizeDesktopPx ?? 0, 22)
                        : Math.min(badge?.icon?.iconSizeMobilePx ?? 0, 18);
                      const styles = badge?.styles;
                      const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                      const shouldUseLineBreak = isDesktopSize
                        ? styles?.manualLineBreakDesktop
                        : styles?.manualLineBreakMobile;
                      const iconStyle: React.CSSProperties = {
                        position: iconType,
                        top: iconPositioning.top ?? 0,
                        left: iconPositioning.left ?? 0,
                        width: iconSize,
                        height: iconSize,
                        ...(iconPosition === 'left' && { marginRight: badge?.icon?.spacingPx ?? 0 }),
                        ...(iconPosition === 'right' && { marginLeft: badge?.icon?.spacingPx ?? 0 }),
                      };
                      const selfClass =
                        containerPosition === 'center'
                          ? 'self-center'
                          : containerPosition === 'right'
                          ? 'self-end'
                          : 'self-start';

                      const iconElement = badge?.icon?.asset && (
                        <img
                          src={`https://${badge?.icon?.asset?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${badge?.icon?.asset?.[selectedLocale]?.name}`}
                          alt=""
                          width={iconSize}
                          height={iconSize}
                          style={iconStyle}
                        />
                      );
                      return (
                        <span
                          key={badge?.id || badge?.badgeText || idx}
                          className={`inline-flex w-fit items-center ${selfClass}`}
                          style={{
                            ...(isGradient
                              ? { backgroundImage: styles?.backgroundGradient }
                              : { backgroundColor: styles?.backgroundColor }),
                            color: styles?.textColor,
                            fontSize: `${!isMobile ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                            fontWeight: styles?.fontWeight,
                            padding: !isMobile ? styles?.paddingMobile : styles?.paddingDesktop,
                            minHeight: `${!isMobile ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                            maxHeight: `${!isMobile ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                            gap: `${!isMobile ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                            borderWidth: `${styles?.borderThickness || 0}px`,
                            borderColor: styles?.borderColor,
                          }}
                        >
                          {iconPosition === 'left' && iconElement}
                          {shouldUseLineBreak ? (
                            <p className="text-center" dangerouslySetInnerHTML={{ __html: label }} />
                          ) : (
                            <p className="text-center">{label?.replace(/<[^>]*>/g, '')}</p>
                          )}{' '}
                          {iconPosition === 'right' && iconElement}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="relative  flex-grow overflow-hidden text-ellipsis text-[18px] font-medium leading-[22px] md:text-[18px] md:leading-[22px]">
                {/* {product?.name} */}
                {`${product?.name} ${productColour ? `- ${productColour}` : ''}`}
              </div>
              <div className="flex flex-col justify-center gap-y-16">
                {numberOfReviews !== 0 && (
                  <div className="flex cursor-pointer items-center justify-center gap-6 py-7">
                    {StarImage && <StarImage />}
                    <span className="text-sm font-normal text-[#1E1E1E]">{numberOfReviews} reviews</span>
                  </div>
                )}
                <div className="mt-4 text-center md:mt-6">
                  <div className="flex flex-nowrap items-center justify-center text-[32px] font-bold leading-[32px] text-[#1e1e1e]">
                    {/* Need to Fix */}
                    {/* <Prices price={selectedVariant?.price} discountedPrice={selectedVariant?.discountedPrice} /> */}
                    <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SmartLink>
        {sortedColorVariants && sortedColorVariants?.length > 0 && (
          <div className="mt-16 flex flex-wrap justify-center gap-6">
            {sortedColorVariants?.map((swatch, index) => (
              <div
                key={index}
                className={`relative inline-block h-[22px] w-[22px] cursor-pointer ${
                  hoveredSwatch === swatch ? '' : ''
                }`}
                onMouseEnter={() => setHoveredSwatch(swatch)}
                onMouseLeave={() => setHoveredSwatch(null)}
                onClick={() => handleColourSelect(swatch, product?.variantCode as any)}
                title={swatch?.colour}
              >
                <CheckedSwatch
                  imgUrl={swatch?.images?.[0]}
                  altText="swatch image"
                  sku={swatch?.sku}
                  isActive={(hoveredSwatch?.sku || selectedSwatch?.sku) === swatch?.sku}
                  colourHexCode={swatch?.colourHexCode ?? ''}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ProductListTile;
